import { AuthApi } from '../auth/authApi';
import { Singleton } from '@/decorators';
import { generateQueryString } from '@/libs';
import type {
  AddSectionGroupRequestDTO,
  GetSectionRequestDTO,
  RemoveSectionGroupRequestDTO,
  ClearSectionResponseDTO,
  SectionGroupResponseDTO,
  SectionResponseDTO,
  UpdateSectionRequestDTO,
  UpdateSectionResponseDTO,
  UpdateStagedFieldsGroupResponseDTO,
  UpdateStagedFieldsGroupRequestDTO,
  UploadFileResponseDTO,
  UploadFileRequestDTO,
  DownloadFileRequestDTO,
  DeleteFileResponseDTO,
  DeleteFileRequestDTO,
  GetSummaryResponseDTO,
  GroupRemovalImpactResponseDTO,
  UpdateSectionConditionalFieldsGroupRequestDTO as UpdateSectionConditionalFieldsGroupRequestDTO,
  UpdateSectionConditionalFieldsGroupResponseDTO,
} from './DTOs';
import type { Result } from '@/types/result.interface';
import type { AppError } from '@/types/errors.interface';
import { wrapHttpCall } from '../utils/apiResultMapper';
import { SAVE_REQUEST_TIMEOUT_MS } from '@/api/base/saveRequestTimeout';

/**
 * Section API (Framework Agnostic)
 *
 * Handles all section-related operations for application forms.
 */
@Singleton
export class SectionApi extends AuthApi {
  constructor() {
    super();
  }

  async getSection(body: GetSectionRequestDTO): Promise<Result<SectionResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.get<SectionResponseDTO>(
      `/ApplicationForm/${body.applicationId}/section/${body.sectionId}`,
    ));
  }

  async updateSection(body: UpdateSectionRequestDTO): Promise<Result<UpdateSectionResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.post<UpdateSectionResponseDTO>(
      `/ApplicationForm/${body.applicationId}/section/${body.sectionId}`,
      body.formField,
      { timeout: SAVE_REQUEST_TIMEOUT_MS },
    ));
  }

  async addSectionGroup(body: AddSectionGroupRequestDTO): Promise<Result<SectionGroupResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.post<SectionGroupResponseDTO>(
      `/ApplicationForm/${body.applicationId}/section/${body.sectionId}/group/${body.groupId}`,
    ));
  }

  async removeSectionGroup(body: RemoveSectionGroupRequestDTO): Promise<Result<SectionGroupResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.delete<SectionGroupResponseDTO>(
      `/ApplicationForm/${body.applicationId}/section/${body.sectionId}/group/${body.groupId}`,
    ));
  }

  async clearSectionGroup(
    applicationId: number,
    sectionId: number,
    groupId: string,
  ): Promise<Result<ClearSectionResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.post<ClearSectionResponseDTO>(
      `/ApplicationForm/${applicationId}/section/${sectionId}/group/${groupId}/clear`,
    ));
  }

  async getGroupRemovalImpact(
    applicationId: number,
    sectionId: number,
    groupId: string,
    clears: boolean,
  ): Promise<Result<GroupRemovalImpactResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.get<GroupRemovalImpactResponseDTO>(
      `/ApplicationForm/${applicationId}/section/${sectionId}/group/${groupId}/removal-impact?clears=${clears}`,
    ));
  }

  async updateStagedFieldsGroup(
    body: UpdateStagedFieldsGroupRequestDTO
  ): Promise<Result<UpdateStagedFieldsGroupResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.post<UpdateStagedFieldsGroupResponseDTO>(
      `/ApplicationForm/${body.applicationId}/section/${body.sectionId}/stagedGroup/${body.formField.groupID}`,
      body.formField,
      { timeout: SAVE_REQUEST_TIMEOUT_MS },
    ));
  }

  async updateSectionConditionalFieldsGroup(
    body: UpdateSectionConditionalFieldsGroupRequestDTO,
  ): Promise<Result<UpdateSectionConditionalFieldsGroupResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.post<UpdateSectionConditionalFieldsGroupResponseDTO>(
      `/ApplicationForm/${body.applicationId}/section/${body.sectionId}/conditionalGroup/${body.formField.groupID}`,
      body.formField,
      { timeout: SAVE_REQUEST_TIMEOUT_MS },
    ));
  }

  async uploadFile(body: UploadFileRequestDTO): Promise<Result<UploadFileResponseDTO, AppError>> {
    const formData = new FormData();
    formData.append('File', body.file);

    return wrapHttpCall(() => this.api.post<UploadFileResponseDTO>(
      `/ApplicationForm/${body.applicationId}/section/${body.sectionId}/uploadFile/${body.itemId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: body.onUploadProgress,
        signal: body.cancelToken, // AbortSignal for cancellation
      },
    ));
  }

  async downloadFile(body: DownloadFileRequestDTO): Promise<Result<unknown, AppError>> {
    const queryString = generateQueryString({ RealName: body.realName });

    return wrapHttpCall(() =>
      this.api.get(`/ApplicationForm/${body.applicationId}/section/${body.sectionId}/downloadFile/${body.itemId}${queryString}`)
    );
  }

  async deleteFile(body: DeleteFileRequestDTO): Promise<Result<DeleteFileResponseDTO, AppError>> {
    const queryString = generateQueryString({ RealName: body.realName });

    return wrapHttpCall(() => this.api.post<DeleteFileResponseDTO>(
      `/ApplicationForm/${body.applicationId}/section/${body.sectionId}/deleteFile/${body.itemId}${queryString}`,
    ));
  }

  async getSummary(applicationId: number, sectionId: number): Promise<Result<GetSummaryResponseDTO, AppError>> {
    return wrapHttpCall(() => this.api.get<GetSummaryResponseDTO>(
      `/ApplicationForm/${applicationId}/section/${sectionId}/summary`,
    ));
  }
}
